import { z } from 'zod';

/** Общие примитивы и огибающие ответов (docs/05 §1). */

export const UuidSchema = z.string().uuid();

export const ErrorCodeSchema = z.enum([
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'WIP_LIMIT_EXCEEDED',
  'ACTIVE_SPRINT_EXISTS',
  'SPRINT_NOT_EMPTY',
  'SPRINT_EMPTY',
  'SPRINT_NOT_ACTIVE',
  'STATUS_IN_USE',
  'LAST_ADMIN',
  'LAST_PROJECT_ADMIN',
  'PROJECT_ARCHIVED',
  'TRANSITION_NOT_ALLOWED',
  'RESOLUTION_REQUIRED',
  'INVALID_HIERARCHY',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_MEDIA_TYPE',
  'RATE_LIMITED',
  'JQL_SYNTAX_ERROR',
  'INVITATION_INVALID',
  'MAIL_NOT_CONFIGURED',
  'MAIL_SEND_FAILED',
  // Публичный REST API и вебхуки (docs/12 §10).
  'INVALID_TOKEN',
  'TOKEN_EXPIRED',
  'TOKEN_REVOKED',
  'INSUFFICIENT_SCOPE',
  'TOKEN_LIMIT_REACHED',
  'IDEMPOTENCY_CONFLICT',
  'WEBHOOK_URL_FORBIDDEN',
  'WEBHOOK_URL_TAKEN',
  'WEBHOOK_LIMIT_REACHED',
  // Wiki проекта (docs/14 §6).
  'WIKI_PAGE_HAS_CHILDREN',
  'WIKI_CYCLE',
  'WIKI_SLUG_TAKEN',
  'WIKI_ARCHIVE_INVALID',
  'WIKI_ARCHIVE_TOO_LARGE',
  'WIKI_IMPORT_INVALID',
  'WIKI_IMPORT_CONFLICT',
  'INTERNAL_ERROR',
]);

export const FieldIssueSchema = z.object({
  path: z.string(),
  message: z.string(),
});

export const ApiErrorSchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    details: z.array(FieldIssueSchema).optional(),
    meta: z.record(z.unknown()).optional(),
    requestId: z.string(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
  });
}

export const IdParamSchema = z.object({ id: UuidSchema });
export const KeyParamSchema = z.object({ key: z.string().min(1) });

/** Даты. */
export const IsoDateTimeSchema = z.string().datetime();
export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Формат YYYY-MM-DD');

/** Константы, дублирующие доменные (docs/03 §2). */
export const IssueTypeSchema = z.enum(['epic', 'story', 'task', 'bug', 'subtask']);
export const StatusCategorySchema = z.enum(['todo', 'in_progress', 'done']);
export const PrioritySchema = z.enum(['lowest', 'low', 'medium', 'high', 'highest']);
export const ResolutionSchema = z.enum([
  'fixed',
  'wont_fix',
  'duplicate',
  'cannot_reproduce',
  'done',
]);
export const SprintStateSchema = z.enum(['future', 'active', 'closed']);
export const ProjectRoleSchema = z.enum(['project_admin', 'member']);
export const GlobalRoleSchema = z.enum(['admin', 'member']);
/** Тип пользователя: человек или AI агент (docs/03 §1). */
export const UserKindSchema = z.enum(['human', 'ai_agent']);
/** Специализация AI агента (docs/03 §1). */
export const AgentRoleSchema = z.enum(['analyst', 'developer']);
export const BoardTypeSchema = z.enum(['kanban', 'scrum']);
export const SwimlaneSchema = z.enum(['none', 'assignee', 'epic', 'priority', 'component']);
export const LinkTypeSchema = z.enum(['blocks', 'relates', 'duplicates', 'clones']);

/** Типы-алиасы для удобства потребителей (сервер и клиент импортируют их). */
export type IssueType = z.infer<typeof IssueTypeSchema>;
export type StatusCategory = z.infer<typeof StatusCategorySchema>;
export type Priority = z.infer<typeof PrioritySchema>;
export type Resolution = z.infer<typeof ResolutionSchema>;
export type SprintState = z.infer<typeof SprintStateSchema>;
export type ProjectRole = z.infer<typeof ProjectRoleSchema>;
export type GlobalRole = z.infer<typeof GlobalRoleSchema>;
export type UserKind = z.infer<typeof UserKindSchema>;
export type AgentRole = z.infer<typeof AgentRoleSchema>;
export type BoardType = z.infer<typeof BoardTypeSchema>;
export type Swimlane = z.infer<typeof SwimlaneSchema>;
export type LinkType = z.infer<typeof LinkTypeSchema>;
